import { apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

type Souhrn = {
  ceka: number;
  polozky: { id: number; text: string; jmeno: string; zdroj: string; created_at: number }[];
};

/** „Dnes" / „Včera" / „28. 9." — kdy požadavek přišel. */
function kdy(ms: number): string {
  const d = new Date(ms);
  const dnes = new Date();
  dnes.setHours(0, 0, 0, 0);
  const den = new Date(d);
  den.setHours(0, 0, 0, 0);
  const rozdil = Math.round((dnes.getTime() - den.getTime()) / 86_400_000);
  if (rozdil === 0) return 'Dnes';
  if (rozdil === 1) return 'Včera';
  return d.getFullYear() === dnes.getFullYear()
    ? `${d.getDate()}. ${d.getMonth() + 1}.`
    : `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

// Jak často obnovit počet. Dotaz samotný je levný (COUNT přes index), ale
// nemá smysl bušit do DB častěji, než má cenu — kontrola chybějících zipů na
// pozadí běží max 1× za 10 min (viz synchronizovatPozadavkyZipu), takže
// častější polling beztak nic nového neukáže. Ruční požadavky od kolegyň se
// tak objeví s pár minutami zpožděním, což je pro nákupní frontu v pohodě.
const OBNOVA_MS = 10 * 60 * 1000;

/**
 * Zvoneček v horní liště: kolik požadavků čeká na objednání (Nová objednávka
 * od kolegyň + automatické u chybějících zipů). Klik rozbalí krátký výpis,
 * odkaz vede na dashboard Nákup.
 */
export function NakupBell() {
  const [souhrn, setSouhrn] = useState<Souhrn | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  const load = useCallback(async () => {
    try {
      setSouhrn(await apiFetch<Souhrn>('/api/vyroba/nakup/souhrn'));
    } catch {
      /* zvoneček není kritický — při chybě jen zůstane poslední stav */
    }
  }, []);

  // Při změně stránky (typicky po vyřízení na Nákupu) a pak pravidelně.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reagujeme právě na změnu cesty
  useEffect(() => {
    load();
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    const t = setInterval(load, OBNOVA_MS);
    return () => clearInterval(t);
  }, [load]);

  // Klik mimo výpis ho zavře.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const ceka = souhrn?.ceka ?? 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Nákup — čeká na objednání: ${ceka}`}
        title={ceka > 0 ? 'Něco čeká na objednání' : 'Vše objednáno'}
        // Oranžová = něco čeká na objednání, zelená = vše vyřízené.
        className={`inline-flex items-center gap-2.5 rounded-full border px-4 py-2 text-base font-bold cursor-pointer transition-colors ${
          ceka > 0
            ? 'bg-warning-light border-warning/40 text-warning hover:brightness-95'
            : 'bg-success-light border-success/40 text-success hover:brightness-95'
        }`}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        <span className="hidden sm:inline">
          {ceka > 0 ? 'Čeká na objednání:' : 'Vše objednáno'}
        </span>
        {ceka > 0 && (
          <span className="min-w-[1.75rem] h-7 px-2 rounded-full bg-warning text-white text-sm font-bold flex items-center justify-center">
            {ceka > 99 ? '99+' : ceka}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-xl shadow-card-hover overflow-hidden">
          <div className="px-4 py-3 border-b border-border-light text-sm font-bold text-text">
            Čeká na objednání: {ceka}
          </div>
          {ceka === 0 ? (
            <p className="px-4 py-5 text-sm text-text-muted text-center">Nic nečeká. 🎉</p>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {(souhrn?.polozky ?? []).map((p) => (
                <li
                  key={p.id}
                  className="flex gap-3 px-4 py-2.5 border-b border-border-light last:border-0"
                >
                  <span
                    className={`shrink-0 w-12 text-[11px] font-bold pt-0.5 ${
                      kdy(p.created_at) === 'Dnes' ? 'text-warning' : 'text-text-muted'
                    }`}
                  >
                    {kdy(p.created_at)}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm text-text line-clamp-2">{p.text}</div>
                    <div className="text-[11px] text-text-muted mt-0.5">
                      {p.zdroj === 'automat' ? '⚙️ automaticky' : p.jmeno}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="p-3 border-t border-border-light">
            <Link
              to="/vyroba/nakup"
              className="block w-full text-center bg-action hover:bg-action-dark text-white text-sm font-bold rounded-lg px-4 py-2.5 transition-colors"
            >
              Otevřít Nákup
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
